import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Crown, Heart, PawPrint, Trophy } from "@phosphor-icons/react";
import api from "@/lib/api";

const TIERS = [
  { name: "Guardian", amount: 1000, color: "#00e5e5", icon: PawPrint },
  { name: "Pack Leader", amount: 5000, color: "#ccff00", icon: Heart },
  { name: "Ranch Builder", amount: 25000, color: "#ff1493", icon: Trophy },
  { name: "Legacy", amount: 100000, color: "#fbbf24", icon: Crown },
];

const TIER_COLOR = Object.fromEntries(TIERS.map((t) => [t.name, t.color]));

function Counter({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const dur = 1400, start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setN(p >= 1 ? value : Math.floor((1 - Math.pow(1 - p, 3)) * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return <span ref={ref}>{n.toLocaleString()}</span>;
}

export const BigDonorWall = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/donors").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return null;

  return (
    <section className="mt-16" data-testid="big-donor-wall">
      {/* Lives Saved counter */}
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black p-10 text-center mb-12">
        <div className="absolute left-1/2 -translate-x-1/2 top-0 h-64 w-64 rounded-full soul-pulse pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,20,147,0.4) 0%, rgba(255,20,147,0) 70%)", filter: "blur(14px)" }} />
        <p className="relative overline text-primary mb-3">Every gift is a life</p>
        <p className="relative font-display font-black text-6xl sm:text-8xl neon-text leading-none" data-testid="lives-saved-count">
          <Counter value={data.lives_saved} />
        </p>
        <p className="relative overline text-muted-foreground mt-3">Lives saved · and counting</p>
      </div>

      {/* Big-donor appeal */}
      <div className="text-center max-w-2xl mx-auto mb-10">
        <p className="overline mb-3" style={{ color: "#fbbf24" }}>The Founders Wall</p>
        <h2 className="font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">Major gifts build <span style={{ color: "#ccff00" }}>Heaven.</span></h2>
        <p className="text-muted-foreground mt-4">A legacy gift doesn't just donate — it builds a fence line, a kennel, a whole acre. Join the names that made the ranch real. Etched forever.</p>
      </div>

      {/* Tier ladder */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-testid="donor-tiers">
        {TIERS.map((t) => (
          <div key={t.name} className="rounded-2xl border p-6 text-center bg-card" style={{ borderColor: `${t.color}55` }} data-testid={`donor-tier-${t.name.replace(/\s/g, "-")}`}>
            <t.icon weight="fill" size={26} className="mx-auto mb-3" style={{ color: t.color }} />
            <p className="font-display font-black uppercase text-lg" style={{ color: t.color }}>{t.name}</p>
            <p className="text-sm text-muted-foreground mt-1">${t.amount.toLocaleString()}+</p>
          </div>
        ))}
      </div>

      {/* Donor wall */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="donor-cards">
        {data.donors.map((d, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06 }}
            className="relative rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 overflow-hidden" data-testid={`donor-card-${i}`}>
            <div className="absolute top-0 left-0 h-1 w-full" style={{ background: TIER_COLOR[d.tier] || "#fff" }} />
            <div className="flex items-center justify-between mb-2">
              <span className="overline px-2.5 py-1 rounded-full" style={{ color: TIER_COLOR[d.tier], border: `1px solid ${TIER_COLOR[d.tier]}55` }}>{d.tier}</span>
              <span className="font-modern text-lg">${Number(d.amount).toLocaleString()}</span>
            </div>
            <h3 className="font-display font-black uppercase text-xl leading-tight">{d.name}</h3>
            {d.message && <p className="text-sm text-muted-foreground mt-2 italic">"{d.message}"</p>}
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default BigDonorWall;
